/**
 * Tax & GST workbench — the second view of the Cash Flow page.
 *
 * Turns the already-tagged bank transactions (category × business/family
 * scope) into: the partnership's net income, the GST/BAS figures, and each
 * partner's personal taxable income + estimated tax. All the maths lives in
 * utils/auTax.ts + utils/taxProfile.ts (unit-tested); this file is presentation
 * + the editable settings that feed those functions.
 *
 * It is a cash-basis ESTIMATE and preparation aid — see the disclaimer banner.
 */

import React, { useMemo, useState } from 'react';
import { useCashFlow } from '../../contexts/CashFlowContext';
import { useTaxConfig } from '../../hooks/useTaxConfig';
import {
  FINANCIAL_YEARS,
  FinancialYear,
  financialYearLabel,
  financialYearRange,
  CapitalAsset,
  DepreciationMethod,
  DEPRECIATION_METHODS,
  DEPRECIATION_METHOD_LABELS,
  capitalAllowanceForYear,
  totalCapitalAllowances,
  INSTANT_ASSET_WRITEOFF,
  SUPER_CONCESSIONAL_CAP,
} from '../../utils/auTax';
import {
  buildPartnershipReturn,
  buildPersonalReturns,
  detectPersonalAssessableIncome,
  resolveCategoryTax,
  CategoryLine,
  PartnershipReturn,
  PersonalReturn,
} from '../../utils/taxProfile';
import { getCategoryLabel } from '../../utils/categories';
import { formatCurrency, formatPct } from './taxFormat';

interface Props {
  /** Jump back to the Cash Flow (overview) tab — used by the data-quality
   *  warnings so the user can go fix unclassified rows. */
  onGoToOverview?: () => void;
}

// ---------------------------------------------------------------------------
// CSV export
// ---------------------------------------------------------------------------

function csvCell(v: string | number): string {
  const s = typeof v === 'number' ? String(v) : v;
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function buildCsv(fy: FinancialYear, pr: PartnershipReturn, people: PersonalReturn[], adjustments: Array<{ label: string; value: number }>): string {
  const rows: Array<Array<string | number>> = [];
  rows.push(['Web Construct — Tax & GST summary']);
  rows.push(['Financial year', financialYearLabel(fy)]);
  rows.push(['Basis', 'Cash (bank movements) — estimate only']);
  rows.push([]);
  rows.push(['PARTNERSHIP RETURN']);
  rows.push(['Section', 'Category', 'Gross (incl GST)', 'GST', 'Ex-GST']);
  pr.income.forEach((l) => rows.push(['Income', l.label, l.gross, l.gst, l.exGst]));
  rows.push(['', 'Assessable income', '', '', pr.totals.assessableIncomeExGst]);
  pr.deductions.forEach((l) => rows.push(['Deduction', l.label, l.gross, l.gst, l.exGst]));
  rows.push(['', 'Total deductions', '', '', pr.totals.deductionsExGst]);
  adjustments.filter((x) => x.value > 0).forEach((x) => rows.push(['', x.label, '', '', x.value]));
  const adjTotal = adjustments.reduce((s, x) => s + (x.value || 0), 0);
  rows.push(['', 'NET PARTNERSHIP INCOME', '', '', Math.round((pr.totals.netIncome - adjTotal) * 100) / 100]);
  rows.push([]);
  rows.push(['GST / BAS']);
  rows.push(['1A GST on sales', pr.totals.gstCollected]);
  rows.push(['1B GST on purchases', pr.totals.gstPaid]);
  rows.push(['Net GST (payable if positive)', pr.totals.gstNet]);
  rows.push([]);
  rows.push(['PERSONAL RETURNS']);
  rows.push(['Partner', 'Distribution', 'Other income', 'Deductions', 'Super', 'Taxable', 'Income tax', 'LITO', 'Medicare', 'Total tax', 'After-tax']);
  people.forEach((p) =>
    rows.push([p.name, p.distribution, p.otherIncome, p.deductions, p.super, p.taxable, p.tax.incomeTax, p.tax.lito, p.tax.medicare, p.tax.totalTax, p.tax.afterTax]),
  );
  return rows.map((r) => r.map(csvCell).join(',')).join('\n');
}

function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------

function MoneyInput({ value, onChange, ariaLabel }: { value: number; onChange: (n: number) => void; ariaLabel: string }) {
  const [text, setText] = useState<string>(value ? String(value) : '');
  React.useEffect(() => { setText(value ? String(value) : ''); }, [value]);
  return (
    <div className="input-group input-group-sm" style={{ maxWidth: 170 }}>
      <span className="input-group-text">$</span>
      <input
        type="number"
        className="form-control"
        aria-label={ariaLabel}
        value={text}
        placeholder="0"
        onChange={(e) => {
          setText(e.target.value);
          const n = parseFloat(e.target.value);
          onChange(Number.isFinite(n) ? n : 0);
        }}
      />
    </div>
  );
}

function LineTable({ lines, valueLabel, registered }: { lines: CategoryLine[]; valueLabel: string; registered: boolean }) {
  if (lines.length === 0) return <div className="text-muted small fst-italic px-3 py-2">None.</div>;
  return (
    <div className="table-responsive">
      <table className="table table-sm align-middle mb-0">
        <thead>
          <tr className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>
            <th>Category</th>
            <th className="text-end">Gross{registered ? ' (incl GST)' : ''}</th>
            {registered && <th className="text-end">GST</th>}
            <th className="text-end">{valueLabel}</th>
            <th className="text-end">#</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l) => (
            <tr key={l.category}>
              <td>{l.label}{!l.gstApplicable && registered && <span className="badge bg-light-secondary text-muted ms-2" title="No GST — input-taxed / GST-free">no GST</span>}</td>
              <td className="text-end">{formatCurrency(l.gross)}</td>
              {registered && <td className="text-end text-muted">{formatCurrency(l.gst)}</td>}
              <td className="text-end fw-semibold">{formatCurrency(l.exGst)}</td>
              <td className="text-end text-muted">{l.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Settings bar
// ---------------------------------------------------------------------------

function SettingsBar({ tax }: { tax: ReturnType<typeof useTaxConfig> }) {
  const { config, update, saving } = tax;
  const splitPctA = Math.round(config.splitA * 100);
  return (
    <div className="card mb-4">
      <div className="card-header py-3 d-flex align-items-center">
        <h6 className="mb-0"><i className="bi bi-sliders me-2" />Settings</h6>
        <span className="ms-auto text-muted small">{saving ? 'Saving…' : 'Saved'}</span>
      </div>
      <div className="card-body">
        <div className="row g-4">
          <div className="col-md-3">
            <label className="form-label small fw-semibold">Financial year</label>
            <select className="form-select form-select-sm" value={config.financialYear} onChange={(e) => update({ financialYear: e.target.value as FinancialYear })}>
              {FINANCIAL_YEARS.map((fy) => (<option key={fy} value={fy}>{fy}</option>))}
            </select>
            <div className="text-muted mt-1" style={{ fontSize: '0.72rem' }}>{financialYearLabel(config.financialYear)}</div>
          </div>
          <div className="col-md-3">
            <label className="form-label small fw-semibold d-block">GST registered</label>
            <div className="form-check form-switch">
              <input className="form-check-input" type="checkbox" checked={config.gstRegistered} onChange={(e) => update({ gstRegistered: e.target.checked })} id="gstReg" />
              <label className="form-check-label small" htmlFor="gstReg">{config.gstRegistered ? 'Yes — lodging a BAS' : 'No — figures stay GST-inclusive'}</label>
            </div>
          </div>
          <div className="col-md-3">
            <label className="form-label small fw-semibold">Dependent children</label>
            <input type="number" min={0} className="form-control form-control-sm" value={config.dependents} onChange={(e) => update({ dependents: Math.max(0, parseInt(e.target.value, 10) || 0) })} style={{ maxWidth: 100 }} />
            <div className="text-muted mt-1" style={{ fontSize: '0.72rem' }}>Lifts the family Medicare threshold.</div>
          </div>
          <div className="col-md-3">
            <label className="form-label small fw-semibold d-block">Wages treatment</label>
            <div className="form-check form-switch">
              <input className="form-check-input" type="checkbox" checked={config.wagesAreEmployees} onChange={(e) => update({ wagesAreEmployees: e.target.checked })} id="empWages" />
              <label className="form-check-label small" htmlFor="empWages">Deductible (employees)</label>
            </div>
            <div className="text-muted mt-1" style={{ fontSize: '0.72rem' }}>Off = partner drawings (not deductible).</div>
          </div>
        </div>

        <hr className="my-4" />

        <div className="row g-4 align-items-end">
          <div className="col-md-3">
            <label className="form-label small fw-semibold">Partner 1 name</label>
            <input className="form-control form-control-sm" value={config.partnerAName} onChange={(e) => update({ partnerAName: e.target.value })} />
          </div>
          <div className="col-md-3">
            <label className="form-label small fw-semibold">Partner 2 name</label>
            <input className="form-control form-control-sm" value={config.partnerBName} onChange={(e) => update({ partnerBName: e.target.value })} />
          </div>
          <div className="col-md-6">
            <label className="form-label small fw-semibold">Profit split — {config.partnerAName} {splitPctA}% / {config.partnerBName} {100 - splitPctA}%</label>
            <input type="range" className="form-range" min={0} max={100} step={5} value={splitPctA} onChange={(e) => update({ splitA: (parseInt(e.target.value, 10) || 0) / 100 })} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Partnership return card
// ---------------------------------------------------------------------------

function TotalRow({ label, value, strong, tone }: { label: string; value: number; strong?: boolean; tone?: 'success' | 'danger' | 'dark' }) {
  const cls = tone === 'success' ? 'text-success' : tone === 'danger' ? 'text-danger' : '';
  return (
    <div className={`d-flex justify-content-between align-items-baseline pt-2 mt-2 border-top ${strong ? 'fw-bold fs-5' : 'fw-semibold'}`}>
      <span>{label}</span>
      <span className={cls}>{formatCurrency(value)}</span>
    </div>
  );
}

function PartnershipReturnCard({ pr, registered, adjustments, onGoToOverview }: { pr: PartnershipReturn; registered: boolean; adjustments: Array<{ label: string; value: number }>; onGoToOverview?: () => void }) {
  const w = pr.warnings;
  const adjTotal = adjustments.reduce((s, x) => s + (x.value || 0), 0);
  const netIncome = Math.round((pr.totals.netIncome - adjTotal) * 100) / 100;
  const hasWarnings = w.uncategorisedBusinessCount > 0 || w.unassignedCount > 0;
  const excl = pr.excluded;
  const [showExcluded, setShowExcluded] = useState(false);
  return (
    <div className="card mb-4">
      <div className="card-header py-3">
        <h6 className="mb-0"><i className="bi bi-building me-2" />Partnership return <span className="text-muted small ms-2">net income {registered ? '(GST-exclusive)' : ''}</span></h6>
      </div>
      <div className="card-body">
        {hasWarnings && (
          <div className="alert alert-warning d-flex flex-wrap align-items-center gap-2 py-2">
            <i className="bi bi-exclamation-triangle-fill" />
            <span className="small">
              {w.uncategorisedBusinessCount > 0 && <> <strong>{w.uncategorisedBusinessCount}</strong> business row(s) still uncategorised ({formatCurrency(w.uncategorisedBusinessGross)}).</>}
              {w.unassignedCount > 0 && <> <strong>{w.unassignedCount}</strong> row(s) have no books assigned ({formatCurrency(w.unassignedGross)}) — they’re not counted here.</>}
              {' '}These understate your figures.
            </span>
            {onGoToOverview && <button className="btn btn-sm btn-warning ms-auto" onClick={onGoToOverview}>Fix in Cash Flow →</button>}
          </div>
        )}

        <div className="row g-4">
          <div className="col-lg-6">
            <div className="text-muted text-uppercase fw-semibold small mb-2">Assessable income</div>
            <LineTable lines={pr.income} valueLabel={registered ? 'Ex-GST' : 'Amount'} registered={registered} />
            <TotalRow label="Assessable income" value={pr.totals.assessableIncomeExGst} tone="success" />
          </div>
          <div className="col-lg-6">
            <div className="text-muted text-uppercase fw-semibold small mb-2">Allowable deductions</div>
            <LineTable lines={pr.deductions} valueLabel={registered ? 'Ex-GST' : 'Amount'} registered={registered} />
            <TotalRow label="Total deductions" value={pr.totals.deductionsExGst} tone="danger" />
          </div>
        </div>

        {adjustments.filter((x) => x.value > 0).map((x) => (
          <div key={x.label} className="d-flex justify-content-between align-items-baseline mt-2 small">
            <span className="text-muted">− {x.label}</span>
            <span className="text-danger fw-semibold">{formatCurrency(x.value)}</span>
          </div>
        ))}
        <div className="mt-3 p-3 rounded bg-light-primary">
          <TotalRow label="Net partnership income" value={netIncome} strong tone={netIncome >= 0 ? 'dark' : 'danger'} />
          <div className="text-muted small mt-1">Distributed to the partners below. The partnership itself pays no income tax.</div>
        </div>

        {/* Excluded / not deductible */}
        <div className="mt-3">
          <button className="btn btn-sm btn-link text-decoration-none px-0" onClick={() => setShowExcluded((v) => !v)}>
            <i className={`bi bi-chevron-${showExcluded ? 'down' : 'right'} me-1`} />
            Excluded — not deductions ({excl.drawings.length + excl.atoPayments.length + excl.nonDeductible.length} categories{excl.internalTransfers.count > 0 ? ` + ${excl.internalTransfers.count} transfers` : ''})
          </button>
          {showExcluded && (
            <div className="border rounded p-3 mt-1 bg-light">
              <p className="small text-muted mb-2">These business-books rows are deliberately kept out of the deduction total:</p>
              {excl.drawings.length > 0 && (<><div className="fw-semibold small mt-2">Partner drawings (not deductible)</div><LineTable lines={excl.drawings} valueLabel="Amount" registered={false} /></>)}
              {excl.atoPayments.length > 0 && (<><div className="fw-semibold small mt-2">ATO payments (income tax / GST / PAYG)</div><LineTable lines={excl.atoPayments} valueLabel="Amount" registered={false} /></>)}
              {excl.nonDeductible.length > 0 && (<><div className="fw-semibold small mt-2">Private / non-deductible</div><LineTable lines={excl.nonDeductible} valueLabel="Amount" registered={false} /></>)}
              {excl.internalTransfers.count > 0 && (<div className="small mt-2">Internal transfers: {excl.internalTransfers.count} row(s), {formatCurrency(excl.internalTransfers.gross)} (netted out).</div>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// GST / BAS card
// ---------------------------------------------------------------------------

function GstBasCard({ pr }: { pr: PartnershipReturn }) {
  const { gstCollected, gstPaid, gstNet } = pr.totals;
  const payable = gstNet >= 0;
  return (
    <div className="card mb-4">
      <div className="card-header py-3">
        <h6 className="mb-0"><i className="bi bi-percent me-2" />GST / BAS <span className="text-muted small ms-2">{financialYearLabel(pr.fy)} · cash basis</span></h6>
      </div>
      <div className="card-body">
        <div className="row g-4 text-center">
          <div className="col-md-4">
            <div className="text-muted small text-uppercase fw-semibold">1A · GST on sales</div>
            <div className="fs-3 fw-bold text-success">{formatCurrency(gstCollected)}</div>
            <div className="text-muted small">collected from customers</div>
          </div>
          <div className="col-md-4">
            <div className="text-muted small text-uppercase fw-semibold">1B · GST on purchases</div>
            <div className="fs-3 fw-bold text-danger">{formatCurrency(gstPaid)}</div>
            <div className="text-muted small">credits on business buys</div>
          </div>
          <div className="col-md-4">
            <div className="text-muted small text-uppercase fw-semibold">Net GST</div>
            <div className={`fs-3 fw-bold ${payable ? 'text-danger' : 'text-success'}`}>{formatCurrency(Math.abs(gstNet))}</div>
            <div className="text-muted small">{payable ? 'payable to the ATO' : 'refund from the ATO'}</div>
          </div>
        </div>
        <div className="alert alert-light border mt-3 mb-0 small text-muted">
          Whole-year figures. If you lodge <strong>quarterly</strong>, filter the Cash Flow tab to each quarter and read the GST off there, or split this net across your BAS periods. Interest and bank fees are treated as GST-free.
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Personal tax card
// ---------------------------------------------------------------------------

function PersonalTaxCard({
  person, otherIncome, deductions, superValue, superCap, onOtherIncome, onDeductions, onSuper,
}: {
  person: PersonalReturn; otherIncome: number; deductions: number; superValue: number; superCap: number;
  onOtherIncome: (n: number) => void; onDeductions: (n: number) => void; onSuper: (n: number) => void;
}) {
  const t = person.tax;
  return (
    <div className="card h-100">
      <div className="card-header py-3">
        <h6 className="mb-0"><i className="bi bi-person me-2" />{person.name}</h6>
      </div>
      <div className="card-body">
        <div className="d-flex justify-content-between small mb-1"><span className="text-muted">Partnership distribution</span><span className="fw-semibold">{formatCurrency(person.distribution)}</span></div>
        <div className="d-flex justify-content-between align-items-center small mb-1">
          <span className="text-muted">+ Other income</span>
          <MoneyInput value={otherIncome} onChange={onOtherIncome} ariaLabel={`${person.name} other income`} />
        </div>
        <div className="d-flex justify-content-between align-items-center small mb-1">
          <span className="text-muted">− Personal deductions</span>
          <MoneyInput value={deductions} onChange={onDeductions} ariaLabel={`${person.name} deductions`} />
        </div>
        <div className="d-flex justify-content-between align-items-center small mb-1">
          <span className="text-muted">− Super contribution <i className="bi bi-info-circle" title="Deductible personal (concessional) super contribution" /></span>
          <MoneyInput value={superValue} onChange={onSuper} ariaLabel={`${person.name} super contribution`} />
        </div>
        {superValue > superCap && (
          <div className="text-danger text-end mb-1" style={{ fontSize: '0.72rem' }}>
            Over the {formatCurrency(superCap)} cap — only deductible if you have carry-forward room.
          </div>
        )}
        <TotalRow label="Taxable income" value={person.taxable} />

        <div className="mt-3 pt-2">
          <div className="d-flex justify-content-between small mb-1"><span className="text-muted">Income tax</span><span>{formatCurrency(t.incomeTax)}</span></div>
          <div className="d-flex justify-content-between small mb-1"><span className="text-muted">− LITO offset</span><span className="text-success">−{formatCurrency(t.lito)}</span></div>
          <div className="d-flex justify-content-between small mb-1"><span className="text-muted">+ Medicare levy (2%)</span><span>{formatCurrency(t.medicare)}</span></div>
          <TotalRow label="Estimated tax payable" value={t.totalTax} tone="danger" />
        </div>

        <div className="mt-3 p-3 rounded bg-light-success d-flex justify-content-between align-items-baseline">
          <span className="fw-semibold">After-tax income</span>
          <span className="fw-bold fs-5 text-success">{formatCurrency(t.afterTax)}</span>
        </div>
        <div className="text-muted small mt-2 d-flex justify-content-between">
          <span>Effective rate {formatPct(t.averageRate)}</span>
          <span>Marginal {formatPct(t.marginalRate)}</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Advanced: per-category tax treatment
// ---------------------------------------------------------------------------

function CategoryTreatmentTable({ pr, tax }: { pr: PartnershipReturn; tax: ReturnType<typeof useTaxConfig> }) {
  const [open, setOpen] = useState(false);
  const { config, setCategoryOverride } = tax;
  // Every category present in the business books this year.
  const categories = useMemo(() => {
    const set = new Set<string>();
    [...pr.income, ...pr.deductions, ...pr.excluded.drawings, ...pr.excluded.atoPayments, ...pr.excluded.nonDeductible].forEach((l) => set.add(l.category));
    return Array.from(set).sort();
  }, [pr]);
  if (categories.length === 0) return null;

  return (
    <div className="card mb-4">
      <div className="card-header py-3 d-flex align-items-center" style={{ cursor: 'pointer' }} onClick={() => setOpen((v) => !v)}>
        <h6 className="mb-0"><i className={`bi bi-chevron-${open ? 'down' : 'right'} me-2`} />Advanced — category tax treatment</h6>
        <span className="ms-auto text-muted small">override deductibility / GST per category</span>
      </div>
      {open && (
        <div className="card-body">
          <p className="text-muted small">Defaults follow ATO norms (drawings & ATO payments non-deductible; interest, bank fees & food GST-free). Override any category here — changes are saved and recompute everything above.</p>
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>
                  <th>Category</th><th className="text-center">Deductible</th><th className="text-center">GST applies</th><th></th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat) => {
                  const eff = resolveCategoryTax(cat, config.categoryOverrides);
                  const overridden = !!config.categoryOverrides[cat];
                  const isIncome = cat.startsWith('income:');
                  return (
                    <tr key={cat}>
                      <td>{getCategoryLabel(cat)}</td>
                      <td className="text-center">
                        {isIncome ? <span className="text-muted small">n/a</span> : (
                          <input type="checkbox" className="form-check-input" checked={eff.deductible} onChange={(e) => setCategoryOverride(cat, { deductible: e.target.checked })} />
                        )}
                      </td>
                      <td className="text-center">
                        <input type="checkbox" className="form-check-input" checked={eff.gstApplicable} onChange={(e) => setCategoryOverride(cat, { gstApplicable: e.target.checked })} />
                      </td>
                      <td className="text-end">{overridden && <button className="btn btn-sm btn-link text-muted p-0" onClick={() => setCategoryOverride(cat, null)}>reset</button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Capital allowances (depreciation) editor
// ---------------------------------------------------------------------------

function CapitalAllowancesCard({ assets, fy, onChange }: { assets: CapitalAsset[]; fy: FinancialYear; onChange: (a: CapitalAsset[]) => void }) {
  const [open, setOpen] = useState(assets.length > 0);
  const total = totalCapitalAllowances(assets, fy);
  const newId = () => `a_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
  const addAsset = (tpl: Partial<CapitalAsset>) => onChange([...assets, { id: newId(), label: '', method: 'immediate', cost: 0, businessUsePct: 1, acquiredFY: fy, ...tpl }]);
  const patch = (id: string, p: Partial<CapitalAsset>) => onChange(assets.map((a) => (a.id === id ? { ...a, ...p } : a)));
  const remove = (id: string) => onChange(assets.filter((a) => a.id !== id));

  return (
    <div className="card mb-4">
      <div className="card-header py-3 d-flex align-items-center" style={{ cursor: 'pointer' }} onClick={() => setOpen((v) => !v)}>
        <h6 className="mb-0"><i className={`bi bi-chevron-${open ? 'down' : 'right'} me-2`} />Capital allowances (depreciation)</h6>
        <span className="ms-auto fw-semibold">{formatCurrency(total)} <span className="text-muted small fw-normal">this year</span></span>
      </div>
      {open && (
        <div className="card-body">
          <p className="text-muted small">Assets not expensed immediately — the building's business-premises share (capital works @2.5%/yr) and the vehicle. These reduce the partnership net income above. Enter each once; the deduction recomputes per year.</p>
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr className="text-muted text-uppercase" style={{ fontSize: '0.7rem' }}>
                  <th>Asset</th><th>Method</th><th className="text-end">Cost (ex-GST)</th><th className="text-end">Business %</th><th>Acquired</th><th className="text-end">Life</th><th className="text-center">Car</th><th className="text-end">{fy} deduction</th><th></th>
                </tr>
              </thead>
              <tbody>
                {assets.map((a) => {
                  const needsLife = a.method === 'primeCost' || a.method === 'diminishingValue';
                  const immediateTooBig = a.method === 'immediate' && a.cost >= (INSTANT_ASSET_WRITEOFF[a.acquiredFY] ?? Infinity);
                  return (
                    <tr key={a.id}>
                      <td><input className="form-control form-control-sm" style={{ minWidth: 140 }} value={a.label} placeholder="e.g. Home-office build" onChange={(e) => patch(a.id, { label: e.target.value })} /></td>
                      <td>
                        <select className="form-select form-select-sm" style={{ minWidth: 175 }} value={a.method} onChange={(e) => patch(a.id, { method: e.target.value as DepreciationMethod })}>
                          {DEPRECIATION_METHODS.map((m) => <option key={m} value={m}>{DEPRECIATION_METHOD_LABELS[m]}</option>)}
                        </select>
                      </td>
                      <td><div className="input-group input-group-sm" style={{ width: 120 }}><span className="input-group-text">$</span><input type="number" className="form-control" value={a.cost || ''} onChange={(e) => patch(a.id, { cost: parseFloat(e.target.value) || 0 })} /></div></td>
                      <td><div className="input-group input-group-sm" style={{ width: 82 }}><input type="number" className="form-control text-end" value={Math.round(a.businessUsePct * 100)} onChange={(e) => patch(a.id, { businessUsePct: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)) / 100 })} /><span className="input-group-text">%</span></div></td>
                      <td><select className="form-select form-select-sm" value={a.acquiredFY} onChange={(e) => patch(a.id, { acquiredFY: e.target.value as FinancialYear })}>{FINANCIAL_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}</select></td>
                      <td className="text-end">{needsLife ? <input type="number" className="form-control form-control-sm text-end" style={{ width: 62 }} value={a.effectiveLifeYears || ''} onChange={(e) => patch(a.id, { effectiveLifeYears: parseFloat(e.target.value) || undefined })} /> : <span className="text-muted">—</span>}</td>
                      <td className="text-center"><input type="checkbox" className="form-check-input" checked={!!a.isCar} onChange={(e) => patch(a.id, { isCar: e.target.checked })} /></td>
                      <td className="text-end fw-semibold">
                        {formatCurrency(capitalAllowanceForYear(a, fy))}
                        {immediateTooBig && <div className="text-danger fw-normal" style={{ fontSize: '0.68rem' }}>≥ $20k — not eligible, use Pool</div>}
                      </td>
                      <td className="text-end"><button className="btn btn-sm btn-link text-danger p-0" onClick={() => remove(a.id)} title="Remove"><i className="bi bi-x-lg" /></button></td>
                    </tr>
                  );
                })}
                {assets.length === 0 && <tr><td colSpan={9} className="text-center text-muted py-3">No capital assets yet — add the ones below.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button className="btn btn-sm btn-outline-primary" onClick={() => addAsset({ label: 'Home-office building works', method: 'capitalWorks', businessUsePct: 0.2, acquiredFY: fy })}>+ Home-office build (2.5%)</button>
            <button className="btn btn-sm btn-outline-primary" onClick={() => addAsset({ label: 'Motor vehicle', method: 'pool', businessUsePct: 0.5, isCar: true, cost: 40000, acquiredFY: '2024-25' })}>+ Vehicle (pool)</button>
            <button className="btn btn-sm btn-light" onClick={() => addAsset({})}>+ Blank asset</button>
          </div>
          <div className="alert alert-light border mt-3 mb-0 small text-muted">
            Instant-write-off assets (&lt; $20k, e.g. your computers) are already expensed in the transactions — don't re-enter them here. Capital works starts once the office is in use, runs 40 years, and the business portion is subject to CGT on sale.
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Extra deductions (home-office occupancy + other business deductions)
// ---------------------------------------------------------------------------

function AdditionalDeductionsCard({ tax }: { tax: ReturnType<typeof useTaxConfig> }) {
  const { config, update } = tax;
  const [open, setOpen] = useState(false);
  const pctLabel = Math.round(config.premisesPct * 100);
  const occ = config.homeMortgageInterest + config.homeRates + config.homeInsurance + config.homeCleaning;
  const occClaim = Math.round(occ * config.premisesPct * 100) / 100;
  const total = Math.round((occClaim + config.otherBusinessDeductions) * 100) / 100;

  const money = (label: string, value: number, set: (n: number) => void, hint?: string) => (
    <div className="d-flex justify-content-between align-items-center mb-2">
      <div className="pe-2"><div className="small">{label}</div>{hint && <div className="text-muted" style={{ fontSize: '0.7rem' }}>{hint}</div>}</div>
      <MoneyInput value={value} onChange={set} ariaLabel={label} />
    </div>
  );

  return (
    <div className="card mb-4">
      <div className="card-header py-3 d-flex align-items-center" style={{ cursor: 'pointer' }} onClick={() => setOpen((v) => !v)}>
        <h6 className="mb-0"><i className={`bi bi-chevron-${open ? 'down' : 'right'} me-2`} />Extra deductions (home office + other)</h6>
        <span className="ms-auto fw-semibold">{formatCurrency(total)} <span className="text-muted small fw-normal">to business</span></span>
      </div>
      {open && (
        <div className="card-body">
          <div className="row g-4">
            <div className="col-lg-6">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="fw-semibold small">Home-office premises use</div>
                <div className="input-group input-group-sm" style={{ width: 92 }}>
                  <input type="number" className="form-control text-end" value={pctLabel} onChange={(e) => update({ premisesPct: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)) / 100 })} />
                  <span className="input-group-text">%</span>
                </div>
              </div>
              <p className="text-muted" style={{ fontSize: '0.72rem' }}>Annual home costs NOT in your bank feed — the {pctLabel}% business share is deducted. (Like the build, that share is CGT-exposed when you sell.)</p>
              {money('Mortgage interest', config.homeMortgageInterest, (n) => update({ homeMortgageInterest: n }), 'Interest only, not principal — usually the biggest one.')}
              {money('Council rates', config.homeRates, (n) => update({ homeRates: n }))}
              {money('Home & contents insurance', config.homeInsurance, (n) => update({ homeInsurance: n }))}
              {money('Cleaning (office area)', config.homeCleaning, (n) => update({ homeCleaning: n }))}
              <div className="d-flex justify-content-between pt-2 border-top fw-semibold small"><span>Occupancy claim @ {pctLabel}%</span><span className="text-success">{formatCurrency(occClaim)}</span></div>
            </div>
            <div className="col-lg-6">
              <div className="fw-semibold small mb-2">Other business deductions</div>
              <p className="text-muted" style={{ fontSize: '0.72rem' }}>Anything real the transactions missed — office furniture/fit-out (business share), written-off bad debts, business share of loan/card interest, or business costs paid on a personal card / cash / PayPal balance.</p>
              {money('Other business deductions (total)', config.otherBusinessDeductions, (n) => update({ otherBusinessDeductions: n }))}
              <div className="alert alert-light border mt-3 mb-0 text-muted" style={{ fontSize: '0.72rem' }}>
                <strong>Squeeze more (all legit):</strong> personal <strong>super</strong> on each partner card (up to $30k each + 5-yr carry-forward) is the biggest lever; <strong>prepay</strong> hosting/insurance before 30 June (small-business 12-month rule); buy needed gear <strong>&lt; $20k</strong> before year-end for the instant write-off.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

export function TaxCentre({ onGoToOverview }: Props) {
  const { transactions, loading } = useCashFlow();
  const tax = useTaxConfig();
  const { config, update } = tax;

  const pr = useMemo(
    () => buildPartnershipReturn(transactions, {
      fy: config.financialYear,
      gstRegistered: config.gstRegistered,
      categoryOverrides: config.categoryOverrides,
      wagesAreEmployees: config.wagesAreEmployees,
    }),
    [transactions, config.financialYear, config.gstRegistered, config.categoryOverrides, config.wagesAreEmployees],
  );

  const personalHint = useMemo(() => detectPersonalAssessableIncome(transactions, config.financialYear), [transactions, config.financialYear]);

  const capitalTotal = useMemo(
    () => totalCapitalAllowances(config.capitalAssets, config.financialYear),
    [config.capitalAssets, config.financialYear],
  );
  const occupancyExtra = Math.round((config.homeMortgageInterest + config.homeRates + config.homeInsurance + config.homeCleaning) * config.premisesPct * 100) / 100;
  const extraDeductions = Math.round((occupancyExtra + config.otherBusinessDeductions) * 100) / 100;
  const netAfterAll = Math.round((pr.totals.netIncome - capitalTotal - extraDeductions) * 100) / 100;
  const superCap = SUPER_CONCESSIONAL_CAP[config.financialYear];

  const [a, b] = useMemo(() => buildPersonalReturns({
    fy: config.financialYear,
    netPartnershipIncome: netAfterAll,
    splitA: config.splitA,
    partnerAName: config.partnerAName,
    partnerBName: config.partnerBName,
    otherIncomeA: config.otherIncomeA,
    otherIncomeB: config.otherIncomeB,
    deductionsA: config.deductionsA,
    deductionsB: config.deductionsB,
    superA: config.superA,
    superB: config.superB,
    dependents: config.dependents,
  }), [netAfterAll, config]);

  const range = financialYearRange(config.financialYear);

  return (
    <div className="tax-centre">
      {/* Disclaimer */}
      <div className="alert alert-info d-flex align-items-start gap-2">
        <i className="bi bi-info-circle-fill mt-1" />
        <div className="small">
          <strong>Estimate &amp; preparation aid — not a lodgement.</strong> Computed on a <strong>cash basis</strong> from your bank CSVs
          for {financialYearLabel(config.financialYear)}. It excludes depreciation / capital purchases, private-use apportionment,
          super, HELP/HECS, the Medicare levy surcharge, and any income not in these accounts. Confirm the figures with your registered tax agent before lodging.
        </div>
      </div>

      {loading ? (
        <div className="text-muted">Loading transactions…</div>
      ) : (
        <>
          <SettingsBar tax={tax} />
          <PartnershipReturnCard
            pr={pr}
            registered={config.gstRegistered}
            adjustments={[
              { label: 'Capital allowances (depreciation)', value: capitalTotal },
              { label: `Home-office occupancy @ ${Math.round(config.premisesPct * 100)}%`, value: occupancyExtra },
              { label: 'Other business deductions', value: config.otherBusinessDeductions },
            ]}
            onGoToOverview={onGoToOverview}
          />
          <CapitalAllowancesCard assets={config.capitalAssets} fy={config.financialYear} onChange={(next) => update({ capitalAssets: next })} />
          <AdditionalDeductionsCard tax={tax} />
          {config.gstRegistered && <GstBasCard pr={pr} />}

          {/* Personal returns */}
          <div className="card mb-4">
            <div className="card-header py-3 d-flex align-items-center flex-wrap gap-2">
              <h6 className="mb-0"><i className="bi bi-people me-2" />Personal returns</h6>
              {personalHint.total > 0 && (
                <span className="ms-auto small text-muted">
                  Detected {formatCurrency(personalHint.total)} assessable income on the family books (e.g. interest).
                  <button className="btn btn-sm btn-link p-0 ms-1 align-baseline" onClick={() => update({ otherIncomeA: config.otherIncomeA + personalHint.total / 2, otherIncomeB: config.otherIncomeB + personalHint.total / 2 })}>split 50/50 →</button>
                </span>
              )}
            </div>
            <div className="card-body">
              <div className="row g-4">
                <div className="col-lg-6">
                  <PersonalTaxCard person={a} otherIncome={config.otherIncomeA} deductions={config.deductionsA} superValue={config.superA} superCap={superCap} onOtherIncome={(n) => update({ otherIncomeA: n })} onDeductions={(n) => update({ deductionsA: n })} onSuper={(n) => update({ superA: n })} />
                </div>
                <div className="col-lg-6">
                  <PersonalTaxCard person={b} otherIncome={config.otherIncomeB} deductions={config.deductionsB} superValue={config.superB} superCap={superCap} onOtherIncome={(n) => update({ otherIncomeB: n })} onDeductions={(n) => update({ deductionsB: n })} onSuper={(n) => update({ superB: n })} />
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-baseline pt-3 mt-2 border-top">
                <span className="fw-semibold">Combined household tax</span>
                <span className="fw-bold fs-5 text-danger">{formatCurrency(a.tax.totalTax + b.tax.totalTax)}</span>
              </div>
            </div>
          </div>

          <CategoryTreatmentTable pr={pr} tax={tax} />

          {/* Export */}
          <div className="card mb-4">
            <div className="card-body d-flex flex-wrap align-items-center gap-3">
              <div className="me-auto">
                <div className="fw-semibold">Export</div>
                <div className="text-muted small">Hand these figures to your tax agent.</div>
              </div>
              <button className="btn btn-light" onClick={() => window.print()}><i className="bi bi-printer me-2" />Print / Save PDF</button>
              <button className="btn btn-primary" onClick={() => downloadCsv(`tax-summary-${config.financialYear}.csv`, buildCsv(config.financialYear, pr, [a, b], [
                { label: 'Capital allowances (depreciation)', value: capitalTotal },
                { label: `Home-office occupancy @ ${Math.round(config.premisesPct * 100)}%`, value: occupancyExtra },
                { label: 'Other business deductions', value: config.otherBusinessDeductions },
              ]))}>
                <i className="bi bi-download me-2" />Download CSV
              </button>
            </div>
          </div>

          <div className="text-muted small text-center mb-4">Covers transactions dated {range.start} → {range.end}.</div>
        </>
      )}
    </div>
  );
}

export default TaxCentre;
